import { addDays, addYears, isBefore, isValid, parse } from 'date-fns';
import {
  isValidPersonDimex,
  isValidPersonId,
  isValidRuc,
} from 'src/functions/identificationMethods';
import { object, ObjectSchema, string } from 'yup';

export const errorMessages = {
  /** Id type */
  idTypeRequired: 'El tipo de identificación es requerido',

  /** Id Number */
  emptyIdNumber: 'El número de identificación es requerido',
  malformedIdNumber: 'Formato de cédula inválida',
  idNumberRequired: 'El número de identificación es requerido',

  /** Names */
  malformedName: 'El nombre no puede contener números',
  nameRequired: 'Nombre requerido',

  /** Last Names */
  malformedLastName: 'El apellido no puede contener números',
  lastNameRequired: 'Apellido requerido',

  /** Email */
  malformedEmail: 'Formato de correo inválido',
  emailRequired: 'El correo es requerido',

  /** Phone Number */
  phoneTooShort: 'Mínimo 7 dígitos',
  malformedPhone: 'Solo puede contener números',
  phoneNumberRequired: 'Número de celular requerido',

  /** Gender */
  genderRequired: 'El género es requerido',

  /** Birthday */
  malformedBirthday: 'La fecha debe tener formato DD/MM/AAAA',
  birthdayTooYoung: 'Debe ser mayor de 18 años',
  birthdayRequired: 'Fecha de nacimiento es requerida',
};

export default function makeValidationSchema(): ObjectSchema {
  const maxDate = addDays(addYears(Date.now(), -18), 1);

  return object().shape({
    idType: string().required(errorMessages.idTypeRequired),
    idNumber: string()
      .when('idType', {
        is: 'N',
        then: string()
          .test(
            'Check id number format',
            'Formato de cédula inválida',
            isValidPersonId,
          )
          .required(errorMessages.emptyIdNumber),
      })
      .when('idType', {
        is: 'U',
        then: string()
          .test(
            'Check DIMEX number format',
            'Debe iniciar con 1 y contener 12 caracteres',
            isValidPersonDimex,
          )
          .required(errorMessages.emptyIdNumber)
          .max(12, 'Debe iniciar con 1 y contener 12 caracteres'),
      })
      .when('idType', {
        is: 'J',
        then: string()
          .test(
            'Check Legal Id number format',
            'Formato de cédula jurídica inválido',
            isValidRuc,
          )
          .required(errorMessages.emptyIdNumber),
      })
      .when('idType', {
        is: 'P',
        then: string().required(errorMessages.emptyIdNumber),
      }),
    names: string()
      .matches(/^([^0-9]*)$/, { message: errorMessages.malformedName })
      .required(errorMessages.nameRequired),
    lastNames: string().when('idType', {
      is: 'J',
      then: string().notRequired(),
      otherwise: string()
        .matches(/^([^0-9]*)$/, { message: errorMessages.malformedLastName })
        .required(errorMessages.lastNameRequired),
    }),
    email: string()
      .email(errorMessages.malformedEmail)
      .required(errorMessages.emailRequired),
    phoneNumber: string()
      .min(7, errorMessages.phoneTooShort)
      .matches(/^[0-9]*$/, { message: errorMessages.malformedPhone })
      .required(errorMessages.phoneNumberRequired),
    gender: string().when('idType', {
      is: 'J',
      then: string().notRequired(),
      otherwise: string().required(errorMessages.genderRequired),
    }),
    birthday: string().when('idType', {
      is: 'J',
      then: string().notRequired(),
      otherwise: string()
        .test(
          'Check format',
          errorMessages.malformedBirthday,
          (birthdayString: string): boolean => {
            const birthdate = parse(birthdayString, 'dd/MM/yyyy', new Date());
            /**
             * a date string such as 12/12/1 gets parsed to Wed Dec 12 00001
             * thus we need to ensure the user is inserting full dd/mm/yyyy
             */
            return isValid(birthdate) && birthdayString.length === 10;
          },
        )
        .test(
          'Check date boundary',
          errorMessages.birthdayTooYoung,
          (birthdayString: string): boolean => {
            const birthdate = parse(birthdayString, 'dd/MM/yyyy', new Date());
            return isBefore(birthdate, maxDate);
          },
        )
        .required(errorMessages.birthdayRequired),
    }),
  });
}
